import type {
	CABreadcrumbsLink,
	CACommandResult,
	CANotificationItem,
	CANotificationType,
} from '@ds/interfaces'
import { NotificationService } from '@ds/services/NotificationService'
import { COUNTDOWN, DELAY } from '@vuejs/helpers/_constants'
import { useAdministratorStore } from '@vuejs/stores/AdministratorStore'
import { useCurriculumsStore } from '@vuejs/stores/CurriculumsStore'
import { useFranchisorStore } from '@vuejs/stores/FranchisorStore'
import { useTeamStore } from '@vuejs/stores/TeamStore'
import { getAgencyId } from '@vuejs/utils/getAgencyId'
import { format } from 'date-fns'
import { reactive } from 'vue'
import type { RouteLocationNormalized } from 'vue-router'

import type { BulkAction } from '@/javascript/components/bulk_actions_dialog'
import type {
	Agency,
	AgencyBackOffice,
	AgencyInformation,
	CurrentUser,
	Language,
} from '@/javascript/interfaces'
import type { CAMember } from '@/javascript/interfaces'
import { standardDateFormat } from '@/javascript/lib/validate_date/process-date-string'

import type { MemberAction } from '../services/administratorDashboard/interfaces'
import api from '../services/api'
import type { Member } from '../services/members/interfaces'
import { getAgencyTeam } from '../services/team'
import type { State } from '../services/trainingCurriculums/interfaces'
import { getTrialDetails } from '../services/trialSeatUpgrade'
import { getFranchisorId } from '../utils/getFranchisorId'
import { getIdFromUrl } from '../utils/getIdFromUrl'
import { useFeatureFlagStore } from './FeatureFlagStore'
import { useMemberTrainingStore } from './MemberTrainingStore'

export const state = reactive({
	agencies: [] as CACommandResult[],
	agency: null as Agency | null | undefined,
	agencyBackOffice: {
		backOfficeIntegrationEnabled: false,
		backOfficeIntegrationProviderName: '',
		marketingProductName: '',
	} as AgencyBackOffice,
	agencyCreatedAt: '',
	agencyId: null as number | null,
	agencyInfo: {} as AgencyInformation,
	agencyMembers: [] as Member[],
	bulkAction: {
		action: '' as MemberAction,
		actionDescriptionText: '',
		actionText: '',
		additionalMembersText: <></>,
		bodyText: '',
		memberIds: [] as number[],
		membersText: '',
	} as BulkAction,
	checkedOnAnotherPage: false,
	currentDate: '',
	currentDateString: '',
	currentUrl: '',
	currentUser: null as CurrentUser | null | undefined,
	eligibleAgenciesUrl: '/api/agencies/eligible_agencies',
	emittedEvent: '',
	franchisorId: null as number | null,
	franchisorLink: [] as CABreadcrumbsLink[],
	franchisorSSOPath: '',
	hasAgency: false,
	hasFranchisorId: false,
	inFreeTrial: false,
	isAdvanceReporting: false,
	isBillingAdmin: false,
	isFranchisor: false,
	isFromTeamPage: false,
	isMemberOnly: false,
	isMobileView: false,
	isMultiLocation: false,
	isSharedCompletionsEnabled: false,
	loadingAgencyInfo: false,
	page: '',
	pageIsDashboard: false,
	pageIsFranchisor: false,
	previousRoute: null as RouteLocationNormalized | null,
	previousRouteUrl: '',
	routeError: '',
	selectAllChecked: false,
	selectAllCheckedOnPage: {} as { [page: number]: boolean },
	selectedCATableMembers: [] as CAMember[],
	selectedCheckboxNotOnPage: false,
	selectedLanguage: '' as Language,
	showAddAllToOrganizations: false,
	showPopover: {
		id: null as number | null,
		name: '' as PopoverOptions,
	},
	showTrainingDashboard: true,
	simpleHeaderLayout: false,
	someSelectAllCheckboxIsTrue: false,
	states: [] as State[],
	teamBreadcrumbLink: [] as CABreadcrumbsLink[],
	twilioSuccessCode: '',
	twilioUserErrorMessages: {} as { [key: string]: string },
	userId: '',
})

const store = {
	get agencies() {
		return state.agencies
	},
	set agencies(value) {
		state.agencies = value
	},

	get agency() {
		return state.agency
	},
	set agency(value) {
		state.agency = value
	},

	get agencyBackOffice(): AgencyBackOffice {
		return state.agencyBackOffice
	},
	set agencyBackOffice(value) {
		state.agencyBackOffice = value
	},

	get agencyCreatedAt() {
		return state.agencyCreatedAt
	},
	set agencyCreatedAt(value) {
		state.agencyCreatedAt = value
	},

	get agencyId() {
		return getAgencyId()
	},

	get agencyInfo() {
		return state.agencyInfo
	},
	set agencyInfo(value) {
		state.agencyInfo = value
	},

	get agencyMembers(): Member[] {
		return state.agencyMembers
	},
	set agencyMembers(value) {
		state.agencyMembers = value
	},

	get bulkAction(): BulkAction {
		return state.bulkAction
	},
	set bulkAction(value) {
		state.bulkAction = value
	},

	get checkedOnAnotherPage() {
		return state.someSelectAllCheckboxIsTrue && state.selectedCheckboxNotOnPage
	},

	get currentDate() {
		return format(new Date(), 'EEE, dd MMMM yyyy hh:mm:ss xxxx')
	},

	get currentDateString() {
		return standardDateFormat(new Date())
	},

	get currentUrl() {
		return window.location.href
	},

	get currentUser() {
		return state.currentUser
	},
	set currentUser(value) {
		state.currentUser = value
	},

	get eligibleAgenciesUrl() {
		return state.eligibleAgenciesUrl
	},

	get emittedEvent() {
		return state.emittedEvent
	},
	set emittedEvent(value) {
		state.emittedEvent = ''
		state.emittedEvent = value
	},

	get franchisorId() {
		return state.currentUser?.franchisor?.id || getFranchisorId()
	},

	get franchisorLink() {
		const franchisorId = state.currentUser?.franchisor?.id || getFranchisorId()

		return [
			{
				active: state.pageIsFranchisor,
				dataTest: 'navigation-breadcrumbs-franchisor-link',
				name: franchisorId ? 'Multi-location Dashboard' : 'Dashboard',
				to: franchisorId
					? `/agencies?franchisor_id=${franchisorId}`
					: '/',
			},
		]
	},

	get franchisorSSOPath() {
		return state.franchisorSSOPath
	},
	set franchisorSSOPath(value) {
		state.franchisorSSOPath = value
	},

	get hasAgency() {
		return (
			!!state.agency?.id ||
			!!state.currentUser?.agency?.id
		) ?? false
	},

	get hasFranchisorId() {
		return !!getFranchisorId()
	},

	get inFreeTrial() {
		return (
			state.agency?.status === 'free_trial' ||
			state.currentUser?.agency?.status === 'free_trial' ||
			state.agencyInfo?.is_in_free_trial || false
		)
	},

	get isAdvanceReporting() {
		return state.isAdvanceReporting
	},
	set isAdvanceReporting(value) {
		state.isAdvanceReporting = value
	},

	get isBillingAdmin() {
		return state.currentUser?.isSuper ||
			state.currentUser?.isFranchisorManager ||
			state.currentUser?.isAgencyBillingManager ||
			state.currentUser?.isAgencyOwner || false
	},

	get isCurriculumDiscoverabilityEnabled() {
		return useFeatureFlagStore().isFeatureEnabled('curriculum_discoverability')
	},

	get isFranchisor() {
		return state.currentUser?.isFranchisorManager || false
	},

	get isMemberOnly() {
		return !state.currentUser?.isSuper &&
			!state.currentUser?.isAgencyBillingManager &&
			!state.currentUser?.isAgencyManager &&
			!state.currentUser?.isFranchisorManager &&
			!state.currentUser?.isMoreThanCaregiver &&
			state.currentUser?.isCaregiver || false
	},

	get isMobileView() {
		const element = document.querySelector('body')

		return element ? element.classList.contains('mobile-view') : false
	},

	get isMultiLocation() {
		return state.page === 'franchisor'
	},

	get isSharedCompletionsEnabled() {
		return state.isSharedCompletionsEnabled
	},
	set isSharedCompletionsEnabled(value) {
		state.isSharedCompletionsEnabled = value
	},

	get isFromTeamPage() {
		return state.previousRouteUrl.includes('team') || state.currentUrl.includes('team')
	},

	get loadingAgencyInfo() {
		return state.loadingAgencyInfo
	},
	set loadingAgencyInfo(value) {
		state.loadingAgencyInfo = value
	},

	get page() {
		return state.page
	},
	set page(value) {
		state.page = value
	},

	get pageIsDashboard() {
		return state.currentUrl.includes('compliance_report')
	},

	get pageIsFranchisor() {
		const franchisorStore = useFranchisorStore()

		return (
			state.currentUrl === `/agencies?franchisor_id=${franchisorStore.franchisorId}` ||
			state.hasFranchisorId && state.agencies.length >= 1 && state.currentUrl === '/agencies' ||
			state.hasFranchisorId && state.agencies.length >= 1
			&& state.currentUrl === `/agencies?franchisor_id=${franchisorStore.franchisorId}`
		)
	},

	get previousRouteUrl() {
		return state.previousRouteUrl
	},
	set previousRouteUrl(value) {
		state.previousRouteUrl = value
	},

	get previousRoute(): RouteLocationNormalized | null {
		return state.previousRoute
	},
	set previousRoute(value) {
		state.previousRoute = value
	},

	get routeError() {
		return state.routeError
	},
	set routeError(value) {
		state.routeError = value
	},

	get selectAllChecked() {
		return state.selectAllChecked
	},
	set selectAllChecked(value) {
		state.selectAllChecked = value
	},

	get selectAllCheckedOnPage(): { [page: number]: boolean } {
		return state.selectAllCheckedOnPage
	},
	set selectAllCheckedOnPage(value) {
		state.selectAllCheckedOnPage = value
	},

	get selectedCATableMembers() {
		return state.selectedCATableMembers
	},
	set selectedCATableMembers(value) {
		state.selectedCATableMembers = value
	},

	get selectedCheckboxNotOnPage() {
		return !state.selectAllCheckedOnPage[useTeamStore().pagination.currentPage]
	},

	get selectedLanguage() {
		return state.selectedLanguage
	},
	set selectedLanguage(value) {
		state.selectedLanguage = value
	},

	get showAddAllToOrganizations() {
		if (!state.currentUser) {
			return false
		}

		return (state.currentUser.isFranchisorManager || state.currentUser.isSuper) && !!state.currentUser.franchisor
	},

	get someSelectAllCheckboxIsTrue() {
		return Object.values(state.selectAllCheckedOnPage).some(value => value === true)
	},

	get showFranchise() {
		if (!state.currentUser) {
			return false
		}

		return state.currentUser.isFranchisorManager || state.currentUser.isSuper
	},

	get showPopover(): { id: number | null, name: PopoverOptions } {
		return state.showPopover
	},
	set showPopover(value) {
		state.showPopover = value
	},

	get showTrainingDashboard() {
		return state.showTrainingDashboard
	},
	set showTrainingDashboard(value) {
		state.showTrainingDashboard = value
	},

	get simpleHeaderLayout() {
		return state.simpleHeaderLayout
	},
	set simpleHeaderLayout(value) {
		state.simpleHeaderLayout = value
	},

	get states(): State[] {
		return [
			{
				abbreviation: 'AL',
				name: 'Alabama',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'AK',
				name: 'Alaska',
				timezone: 'Alaska',
			},
			{
				abbreviation: 'AZ',
				name: 'Arizona',
				timezone: 'Mountain Time (US & Canada)',
			},
			{
				abbreviation: 'AR',
				name: 'Arkansas',
				timezone: 'Mountain Time (US & Canada)',
			},
			{
				abbreviation: 'CA',
				name: 'California',
				timezone: 'Pacific Time (US & Canada)',
			},
			{
				abbreviation: 'CO',
				name: 'Colorado',
				timezone: 'Mountain Time (US & Canada)',
			},
			{
				abbreviation: 'CT',
				name: 'Connecticut',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'DE',
				name: 'Delaware',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'DC',
				name: 'District of Columbia',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'FL',
				name: 'Florida',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'GA',
				name: 'Georgia',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'HI',
				name: 'Hawaii',
				timezone: 'Hawaii',
			},
			{
				abbreviation: 'ID',
				name: 'Idaho',
				timezone: 'Mountain Time (US & Canada)',
			},
			{
				abbreviation: 'IL',
				name: 'Illinois',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'IN',
				name: 'Indiana',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'IA',
				name: 'Iowa',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'KS',
				name: 'Kansas',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'KY',
				name: 'Kentucky',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'LA',
				name: 'Louisiana',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'ME',
				name: 'Maine',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'MD',
				name: 'Maryland',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'MA',
				name: 'Massachusetts',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'MI',
				name: 'Michigan',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'MN',
				name: 'Minnesota',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'MS',
				name: 'Mississippi',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'MO',
				name: 'Missouri',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'MT',
				name: 'Montana',
				timezone: 'Mountain Time (US & Canada)',
			},
			{
				abbreviation: 'NE',
				name: 'Nebraska',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'NV',
				name: 'Nevada',
				timezone: 'Pacific Time (US & Canada)',
			},
			{
				abbreviation: 'NH',
				name: 'New Hampshire',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'NJ',
				name: 'New Jersey',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'NM',
				name: 'New Mexico',
				timezone: 'Mountain Time (US & Canada)',
			},
			{
				abbreviation: 'NY',
				name: 'New York',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'NC',
				name: 'North Carolina',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'ND',
				name: 'North Dakota',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'OH',
				name: 'Ohio',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'OK',
				name: 'Oklahoma',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'OR',
				name: 'Oregon',
				timezone: 'Pacific Time (US & Canada)',
			},
			{
				abbreviation: 'PA',
				name: 'Pennsylvania',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'RI',
				name: 'Rhode Island',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'SC',
				name: 'South Carolina',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'SD',
				name: 'South Dakota',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'TN',
				name: 'Tennessee',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'TX',
				name: 'Texas',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'UT',
				name: 'Utah',
				timezone: 'Mountain Time (US & Canada)',
			},
			{
				abbreviation: 'VT',
				name: 'Vermont',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'VA',
				name: 'Virginia',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'WA',
				name: 'Washington',
				timezone: 'Pacific Time (US & Canada)',
			},
			{
				abbreviation: 'WV',
				name: 'West Virginia',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'WI',
				name: 'Wisconsin',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'WY',
				name: 'Wyoming',
				timezone: 'Mountain Time (US & Canada)',
			},
			{
				abbreviation: 'PR',
				name: 'Puerto Rico',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'USVI',
				name: 'U.S. Virgin Islands',
				timezone: 'Atlantic Time (Canada)',
			},
			{
				abbreviation: 'AB',
				name: 'Alberta',
				timezone: 'Mountain Time (US & Canada)',
			},
			{
				abbreviation: 'BC',
				name: 'British Columbia',
				timezone: 'Pacific Time (US & Canada)',
			},
			{
				abbreviation: 'MB',
				name: 'Manitoba',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'NB',
				name: 'New Brunswick',
				timezone: 'Atlantic Time (Canada)',
			},
			{
				abbreviation: 'NL',
				name: 'Newfoundland and Labrador',
				timezone: 'Newfoundland',
			},
			{
				abbreviation: 'NS',
				name: 'Nova Scotia',
				timezone: 'Atlantic Time (Canada)',
			},
			{
				abbreviation: 'ON',
				name: 'Ontario',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'PE',
				name: 'Prince Edward Island',
				timezone: 'Atlantic Time (Canada)',
			},
			{
				abbreviation: 'QC',
				name: 'Quebec',
				timezone: 'Eastern Time (US & Canada)',
			},
			{
				abbreviation: 'SK',
				name: 'Saskatchewan',
				timezone: 'Central Time (US & Canada)',
			},
			{
				abbreviation: 'NU',
				name: 'Nunavut',
				timezone: 'Central Time (US & Canada)',
			},
		]
	},

	get teamBreadcrumbLink(): CABreadcrumbsLink[] {
		const goBackToPage = window.sessionStorage.getItem('goBackToPage')

		const showInsightsLink = !goBackToPage?.includes('team') &&
			!!window.sessionStorage.getItem('selectedGroupId') &&
			state.page !== 'team' &&
			!state.previousRouteUrl.includes('team')

		const hideTeamAndInsightsLinks = state.page === 'catalog'

		switch (true) {
			case hideTeamAndInsightsLinks:
				return []

			case state.page === 'designSystem':
				return [
					{
						active: false,
						dataTest: 'navigation-breadcrumbs-design-system',
						name: 'Design System',
						to: `/design-system`,
					},
				]

			case showInsightsLink:
				return [
					{
						active: false,
						dataTest: 'navigation-breadcrumbs-insights',
						name: 'Insights',
						to: `/agencies/${getAgencyId()}/compliance_report`,
					},
				]

			default:
				return [
					{
						active: false,
						dataTest: 'navigation-breadcrumbs-team',
						name: 'Team',
						to: `/agencies/${getAgencyId()}/team`,
					},
				]
		}
	},

	get twilioSuccessCode() {
		return state.twilioSuccessCode
	},
	set twilioSuccessCode(value) {
		state.twilioSuccessCode = value
	},

	get twilioUserErrorMessages(): { [key: string]: string } {
		return state.twilioUserErrorMessages
	},
	set twilioUserErrorMessages(value) {
		state.twilioUserErrorMessages = value
	},

	get userId() {
		return state.userId
	},
	set userId(value) {
		state.userId = value
	},

	// Actions
	checkAndDisplaySessionStorageMessage() {
		const successMessage = window.sessionStorage.getItem('successMessage')
		const sharedCompletionsFound = window.sessionStorage.getItem('sharedCompletionsFound')
		const errorMessage = window.sessionStorage.getItem('errorMessage')

		if (successMessage) {
			this.onHandleSuccessMessages({ message: successMessage })
			window.sessionStorage.removeItem('successMessage')
		}

		if (errorMessage) {
			this.onHandleErrorMessages({ error: errorMessage })
			window.sessionStorage.removeItem('errorMessage')
		}

		if (sharedCompletionsFound) {
			this.onHandleSuccessMessages({ message: sharedCompletionsFound, type: 'info' })
			window.sessionStorage.removeItem('sharedCompletionsFound')
		}
	},

	clearSelectedValues() {
		state.selectedCATableMembers = []
		state.selectAllCheckedOnPage = {}
		state.selectAllChecked = false
	},

	closeModal() {
		state.emittedEvent = ''
		state.selectedCATableMembers = []

		useAdministratorStore().selectedMembers = []
		useMemberTrainingStore().resetData()
		useCurriculumsStore().resetData()
	},

	closePopover() {
		state.showPopover = { id: null, name: '' }
	},

	countDownTimer({
		newMessage,
		response,
	}: {
		newMessage?: string,
		response?: any
	}) {
		let countdown = COUNTDOWN
		const interval = setInterval(() => {
			countdown--
			if (newMessage) { this.onHandleSuccessMessages({ message: `${newMessage} Refreshing page in ${countdown} seconds.` }) }

			if (response) {
				this.onHandleSuccessMessages({ response: `${response.data?.success_messages} Refreshing page in ${countdown} seconds.` })
			}

			if (countdown === 0) {
				clearInterval(interval)
				window.location.reload()
			}
		}, DELAY.ONE_SECOND)
	},

	formatSeatsUsed(seatUsed = 0): number {
		return Math.trunc(seatUsed) ?? 0
	},

	getAPIErrorMessage(error: any, defaultErrorMessage = 'An error occurred, please try again'): string[] | string {
		return error.response?.data?.message ||
			error.message ||
			error.responseJSON?.message ||
			defaultErrorMessage
	},

	getAPISuccessMessage(response: any, defaultSuccessMessage = 'Successfully completed'): string[] | string {
		return response.data?.success ||
			response.responseText ||
			response.data?.message ||
			response.message ||
			response.data?.success_messages ||
			response.success ||
			response.message ||
			response.responseJSON?.message ||
			response.data ||
			response ||
			defaultSuccessMessage
	},

	async getAgencyInfo() {
		state.loadingAgencyInfo = true
		try {
			const id = getIdFromUrl()

			if (id !== 0) {
				const response = await getTrialDetails(api, `${id}`)
				const { data } = response

				if (data.agency) {
					state.agencyInfo = {
						...state.agencyInfo,
						days_left: data.agency.days_left,
						is_expired: data.agency.is_expired,
						total_trial_days: data.agency.total_trial_days,
					}
				}
			}
		} catch (error) {
			this.onHandleErrorMessages({ error })
		} finally {
			state.loadingAgencyInfo = false
		}

	},

	async fillAgencyBackOffice() {
		try {
			const response = await getAgencyTeam(api, getAgencyId())

			state.agencyBackOffice = {
				backOfficeIntegrationEnabled: response.agency?.back_office_integration_enabled,
				backOfficeIntegrationProviderName: response.agency?.back_office_integration_provider_name,
				marketingProductName: response.agency?.marketing_product_name,
			}
		} catch (error) {
			this.onHandleErrorMessages({ error })
		}
	},

	getTwilioUserErrorMessage(errorCode: string): string {
		return state.twilioUserErrorMessages[errorCode]
	},

	hasTwilioUserError(errorCode: string): boolean {
		return state.twilioUserErrorMessages.hasOwnProperty(errorCode)
	},

	identifyPage(url: string) {
		const teamPagePattern = /^\/agencies\/\d+\/team\/?$/

		const groupMembersPagePattern = /^\/agencies\/\d+\/team\/\d+\/members\/?(\d+\/?)?$/

		if (teamPagePattern.test(url)) {
			return 'teamPage'
		} else if (groupMembersPagePattern.test(url)) {
			return 'groupMembersPage'
		} else {
			return 'unknownPage'
		}
	},

	notify(notification: CANotificationItem) {
		if (!notification.message) {
			return
		}

		switch (notification.type) {
			case 'error':
				console.error(notification.message)
				NotificationService.methods.error({ ...notification, duration: notification.duration ?? DELAY.FIVE_SECONDS })
				break

			case 'info':
				NotificationService.methods.info({ ...notification, duration: notification.duration ?? DELAY.FIVE_SECONDS })
				break

			case 'success':
				NotificationService.methods.success({ ...notification, duration: notification.duration ?? DELAY.FIVE_SECONDS })
				break

			default:
				NotificationService.methods.default({ ...notification, duration: notification.duration ?? DELAY.FIVE_SECONDS })
				break
		}
	},

	onHandleErrorMessages({ duration, error }: { duration?: number, error: any }) {
		if (!!error && !!error.name && error.name === 'SecurityError') {
			this.notify({
				duration,
				message: error.message ?? error,
				type: 'error',
			})

			return
		}

		const errorMessages = error.data?.error ??
			error.response?.data?.error ??
			error.response?.data?.errors ??
			error.response?.data?.messages ??
			error.response?.data?.message ??
			error.response?.error ??
			error.response?.errors ??
			error.data?.error_messages ??
			error.responseJSON?.message ??
			error.message ??
			error.json ??
			error ??
			'An error occurred. Please try again.'

		const message = typeof error === 'string' ? error : errorMessages

		if (typeof message === 'string') {
			this.notify({ message: message, type: 'error' })
		} else if (Array.isArray(message) && typeof message[0] === 'string') {
			for (const issue of message) {
				this.notify({ message: issue, type: 'error' })
			}
		} else {
			for (const element in message) {
				if (message[element] && Array.isArray(message[element])) {
					for (const issue of message[element]) {
						this.notify({ message: issue, type: 'error' })
					}
				}
			}
		}
	},

	onHandleSuccessMessages({
		duration,
		message,
		response,
		type = 'success',
	}: {
		duration?: number,
		message?: string,
		response?: any,
		type?: CANotificationType,
	}) {
		if (message) {
			this.notify({
				duration,
				message,
				type,
			})
		} else if (response) {
			const successes = response.data?.success ??
				response.responseText ??
				response.data?.message ??
				response.message ??
				response.data?.success_messages ??
				response.success ??
				response.message ??
				response.responseJSON?.message ??
				response.data ??
				response ??
				'Successfully completed!'

			const errorMessage = response.error

			if (typeof successes === 'string') {
				this.notify({
					duration,
					message: successes,
					type,
				})
			} else if (Array.isArray(successes)) {
				successes.forEach((message: string) => this.notify({
					duration,
					message,
					type,
				}))
			}

			if (errorMessage) {
				this.notify({
					duration,
					message: errorMessage,
					type: 'error',
				})
			}
		}
	},

	onHandleTextMessageResponseError({ response }: { response?: any }) {
		const errors = response?.data?.error_messages ?? response?.data?.error ?? response.error

		if (typeof errors === 'string') {
			this.notify({ message: errors, type: 'error' })
		} else {
			!!errors && errors.map((message: 'error') => this.notify({ message, type: 'error' }))
		}
	},

	openSendBulkMessage() {
		state.emittedEvent = 'send-bulk-message'
	},

	openSendGroupMessage() {
		state.emittedEvent = 'send-group-message'
	},

	openSendMemberMessage() {
		useAdministratorStore().selectedMembers = []
		state.emittedEvent = 'send-member-message'
	},

	resetCAModal() {
		state.emittedEvent = ''
	},

	scrollToTop(scrollContainerId?: string) {
		const scrollContainer = scrollContainerId ? document.getElementById(scrollContainerId) : undefined

		if (scrollContainer) {
			if (scrollContainer.scrollHeight > scrollContainer.clientHeight) {
				scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
			}
		} else {
			window.scrollTo({ top: 0, behavior: 'smooth' })
		}
	},
}

export default store

export type PopoverOptions
	= '' | 'Edit name' | 'Manage due date'
